import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { ProjectEntity } from '@api/entities';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useTheme } from '@context/themeContext';
import { themeColors } from '@styles/theme';
import { ImagePlaceholder } from '@components/icons/icons/ImagePlaceholder';
import { PencilIcon } from '@components/icons/icons/PencilIcon';
import { IconButton } from '@components/buttons/IconButton/IconButton';
import { CloseIcon } from '@components/icons/icons/CloseIcon';

interface Props {
    projects: Array<ProjectEntity>;
    onModelClickHandler: any;
    setProjectEditing: Dispatch<SetStateAction<ProjectEntity | null>>;
}

const ProjectsGrid: React.FC<Props> = ({ projects, onModelClickHandler, setProjectEditing }) => {
    const { t } = useTranslation();
    const { theme } = useTheme();

    const projectEditHandler = useCallback(
        (event, project: ProjectEntity) => {
            event.stopPropagation();
            event.preventDefault();

            setProjectEditing(project);
        },
        [setProjectEditing]
    );

    return (
        <div className="grid-wrapper">
            {projects.map(project => {
                return (
                    <Link
                        key={project.id}
                        onClick={() => onModelClickHandler(project.projectName)}
                        to={`/${project.id}`}
                    >
                        <div
                            className={`grid-elements ${theme}`}
                            data-testid={`project-row_${project.id}`}
                        >
                            <div className="grid-elements-content">
                                <div className={`flex-img ${theme}`}>
                                    <ImagePlaceholder
                                        width={95}
                                        height={95}
                                        color={
                                            theme === 'light'
                                                ? themeColors.textGrey
                                                : themeColors.navyBlue
                                        }
                                        backgroundColor={
                                            theme === 'light'
                                                ? themeColors.lightGrey
                                                : themeColors.grey3
                                        }
                                    />
                                </div>
                                <div className="card-content">
                                    <div className="card-text">
                                        <div className={`grid-elements-content__header ${theme}`}>
                                            {project.projectName}
                                        </div>
                                        <div className={`content-text ${theme}`}>
                                            <span>{t('common.last-update-date')}:</span>
                                            <span>
                                                {moment(project.updateTs).format(
                                                    'YYYY-MM-DD HH:mm:ss'
                                                )}
                                            </span>
                                        </div>
                                        <div className={`content-text ${theme}`}>
                                            <span>{t('projects-main.completed')}:</span>
                                            <span>{project.projectExecutionStats?.completed}</span>
                                        </div>
                                        <div className={`content-text ${theme}`}>
                                            <span>{t('projects-main.active')}:</span>
                                            <span>
                                                {project.projectExecutionStats?.running || (
                                                    <CloseIcon
                                                        width={16}
                                                        height={16}
                                                        color={themeColors.white}
                                                    />
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-buttons">
                                        <IconButton
                                            icon={PencilIcon}
                                            onClick={event => projectEditHandler(event, project)}
                                            data-testid={`edit_project_${project.id}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Link>
                );
            })}
        </div>
    );
};
export default ProjectsGrid;
